import path from 'node:path'
import type { FileNode } from '@kubb/ast'
import { definePlugin } from '@kubb/core'
import type { Config, NormalizedPlugin, Plugin } from '@kubb/core'
import type { BarrelConfig, BarrelType, PluginBarrelConfig } from './types.ts'
import { buildBarrelIndex, getBarrelFiles, getPluginOutputPrefix, isExcludedPath } from './utils.ts'

/**
 * Applies a plugin's configured `output.banner`/`footer` to a barrel file, flagged as `isBarrel`.
 *
 * Resolves through the plugin's own resolver, and only when the plugin explicitly sets a
 * banner/footer, so barrels stay banner-free by default and never inherit the implicit
 * "Generated by Kubb" notice.
 */
function withBarrelBannerFooter({ file, plugin, config }: { file: FileNode; plugin: NormalizedPlugin; config: Config }): FileNode {
  const output = plugin.options?.output
  const resolver = plugin.resolver
  if (!resolver) return file

  const hasBanner = output?.banner !== undefined
  const hasFooter = output?.footer !== undefined
  if (!hasBanner && !hasFooter) return file

  const context = { output, config, file: { path: file.path, baseName: file.baseName, isBarrel: true } }
  return {
    ...file,
    banner: hasBanner ? resolver.default.banner(undefined, context) : file.banner,
    footer: hasFooter ? resolver.default.footer(undefined, context) : file.footer,
  }
}

declare global {
  namespace Kubb {
    interface PluginOptionsRegistry {
      output: {
        /**
         * Barrel configuration for this plugin's output.
         * Set to `{ type: 'named' | 'all' }` to opt this plugin into a barrel. Set to `false`
         * (the default) to disable barrel generation for this plugin entirely, which also
         * excludes the plugin's files from the root barrel.
         *
         * Falls back to `config.output.barrel` when omitted.
         *
         * @default false
         */
        barrel?: PluginBarrelConfig | false
      }
    }
    interface ConfigOptionsRegistry {
      output: {
        /**
         * Barrel configuration for the root barrel file at `config.output.path/index.ts`.
         * Set to `{ type: 'named' | 'all' }` to opt into a root barrel. Individual plugins can
         * override this via their own `output.barrel`.
         *
         * @default false
         */
        barrel?: BarrelConfig | false
      }
    }
  }
}

/**
 * Canonical plugin name for `@kubb/plugin-barrel`. Used for driver lookups
 * and to guard the `kubb:plugin:end` handler against reacting to its own lifecycle hook.
 */
export const pluginBarrelName = 'plugin-barrel' satisfies Plugin['name']

type PendingBarrel = {
  plugin: NormalizedPlugin
  target: string
  barrelType: BarrelType
  nested: boolean
}

/**
 * Generates an `index.ts` for every plugin output directory and one root
 * barrel at `config.output.path/index.ts` after the build completes. Ships
 * with Kubb and is registered by default in `defineConfig`, but generates
 * nothing until a barrel is configured.
 *
 * Each plugin inherits `output.barrel` from `config.output.barrel` (which
 * defaults to `false`, no barrel). Set `barrel: { type: 'named' | 'all' }` on
 * the root config, a plugin, or both to opt in; a plugin-level `false`
 * overrides an enabled root barrel and also excludes that plugin's files
 * from the root barrel.
 *
 * A plugin with `output.mode: 'file'` gets no per-plugin barrel, since its output
 * is a single file. The root barrel re-exports that file directly.
 *
 * @example
 * ```ts
 * import { defineConfig } from '@kubb/core'
 * import { pluginBarrel } from '@kubb/plugin-barrel'
 * import { pluginTs } from '@kubb/plugin-ts'
 * import { pluginZod } from '@kubb/plugin-zod'
 *
 * export default defineConfig({
 *   input: './petStore.yaml',
 *   output: { path: 'src/gen', barrel: { type: 'named' } },
 *   plugins: [
 *     pluginTs({ output: { path: 'types', barrel: { type: 'all' } } }),
 *     pluginZod({ output: { path: 'schemas' } }),
 *     pluginBarrel(),
 *   ],
 * })
 * ```
 */
export const pluginBarrel = definePlugin(() => {
  const excludedPrefixes = new Set<string>()
  const pendingBarrels: Array<PendingBarrel> = []

  return {
    name: pluginBarrelName,
    enforce: 'post' as const,
    hooks: {
      'kubb:plugin:end'({ plugin, config }) {
        // Skip reactions to the barrel plugin's own lifecycle hook
        if (plugin.name === pluginBarrelName) return

        const pluginBarrelOpt = plugin.options.output?.barrel
        const configBarrel = config.output.barrel

        // Root config barrel doesn't have nested, so we add it
        const barrelConfig: PluginBarrelConfig | false = (() => {
          if (pluginBarrelOpt !== undefined) return pluginBarrelOpt
          if (configBarrel !== undefined) return configBarrel === false ? false : { ...configBarrel, nested: false }
          return false
        })()

        if (barrelConfig === false) {
          excludedPrefixes.add(getPluginOutputPrefix(plugin, config))
          return
        }

        // `mode: 'file'` writes a single file, so there is no directory to barrel. The root barrel
        // re-exports that file as a direct leaf of `config.output.path`.
        if (plugin.options.output.mode === 'file') {
          return
        }

        const base = path.resolve(config.root, config.output.path)
        const target = path.resolve(base, plugin.options.output.path)
        const relative = path.relative(base, target)
        if (relative.startsWith('..') || path.isAbsolute(relative)) {
          throw new Error('Invalid output path')
        }

        // Only the target directory and barrel strategy are recorded here. The actual file-set
        // scan and directory-tree build happen once, in `kubb:plugins:end`, and are shared by
        // every plugin barrel plus the root barrel instead of repeating per plugin.
        pendingBarrels.push({ plugin, target, barrelType: barrelConfig.type, nested: barrelConfig.nested ?? false })
      },
      'kubb:plugins:end'({ files, config, upsertFile }) {
        const rootBarrelConfig = config.output.barrel ?? false
        const outputPath = path.resolve(config.root, config.output.path)

        // A `barrel: false` plugin gets no barrel and stays out of the root, so drop its files
        // once here. Every barrel below then derives from a single index of what remains.
        const relevantFiles = excludedPrefixes.size === 0 ? files : files.filter((f) => !isExcludedPath(f.path, excludedPrefixes))
        excludedPrefixes.clear()

        const index = buildBarrelIndex(outputPath, relevantFiles)

        for (const { plugin, target, barrelType, nested } of pendingBarrels) {
          for (const file of getBarrelFiles({ index, targetPath: target, barrelType, nested, recursive: true })) {
            upsertFile(withBarrelBannerFooter({ file, plugin, config }))
          }
        }
        pendingBarrels.length = 0

        if (rootBarrelConfig === false) return

        for (const file of getBarrelFiles({ index, barrelType: rootBarrelConfig.type })) {
          upsertFile(file)
        }
      },
    },
  }
})
